/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, ReactNode, CSSProperties } from "react";
import Markdown from "react-markdown";
import styled from "styled-components";

import { useTheme } from "theme/ThemeCreator";
import { Theme } from "theme/types";

import styles from "./styles.module.scss";
import { getVariantStyles } from "./utils";

type Props = {
  id?: string;
  variant?: string;
  children: string | string[] | number | undefined;
  color?: string;
  ellipsis?: boolean;
  className?: string;
  breakWord?: boolean;
  useMarkdown?: boolean;
  linkColor?: string;
  style?: CSSProperties;
};

export type { Props as LabelProps };

type StyledProps = {
  theme: Theme;
  variantStyles: { [key: string]: any };
  children: ReactNode | Element;
};

const StyledLabel = styled.div<StyledProps>`
  ${({ variantStyles }) => {
    const { tablet, mobile } = variantStyles;

    return `
      @media ${tablet?.media} {
        font-size: ${tablet?.fontSize} !important;
      }

      @media ${mobile?.media} {
        font-size: ${mobile?.fontSize} !important;
      }
    `;
  }}
`;

const Label = forwardRef<HTMLDivElement, Props>(
  (
    {
      variant = "body",
      children,
      color = "white",
      ellipsis = false,
      className = "",
      breakWord = true,
      id,
      useMarkdown,
      linkColor = "link",
      style
    },
    ref
  ) => {
    const theme = useTheme();

    const { colors } = theme.label;

    const variantStyles = getVariantStyles(theme, variant);

    return useMarkdown && typeof children === "string" ? (
      <Markdown
        components={{
          li: ({ ...props }) => (
            <li
              className={className}
              {...props}
              style={{ color: colors[linkColor], ...style, ...variantStyles }}
            />
          ),
          p: ({ ...props }) => (
            <StyledLabel
              variantStyles={variantStyles}
              theme={theme}
              className={`${className} ${styles.base} ${
                ellipsis && styles.ellipsis
              } ${breakWord && styles.breakWord}`}
              style={{ color: colors[color], ...style, ...variantStyles }}
              {...props}
            />
          ),
          a: ({ ...props }) => (
            <a
              {...props}
              style={{ color: colors[linkColor], textDecoration: "underline" }}
            />
          )
        }}
      >
        {children}
      </Markdown>
    ) : (
      <StyledLabel
        variantStyles={variantStyles}
        ref={ref}
        id={id}
        className={`${className} ${styles.base} ${
          ellipsis && styles.ellipsis
        } ${breakWord && styles.breakWord}`}
        style={{ color: colors[color], ...style, ...variantStyles }}
      >
        {children !== undefined ? String(children) : ""}
      </StyledLabel>
    );
  }
);

Label.defaultProps = {
  id: undefined,
  color: "black",
  variant: "body",
  ellipsis: false,
  className: "",
  breakWord: true,
  useMarkdown: false,
  linkColor: "link",
  style: {}
};

export default Label;
